import React, {lazy, Suspense} from "react"

import {EntityType} from "../../../../gd/const/item"
import ProjectSession from "../../../../model2/project/project-session"
import UILoading from "../../../loading/loading"

// lazy -- project-finder pulls in select-parent-new's project tree, which transitively reaches
// model2/doc/document.ts (fields.ts <-> fields-collection.ts <-> fields-factory.ts <-> resources.ts
// is an existing circular dependency); loading it only once this popover actually opens keeps it
// out of the always-loaded sidebar "More" menu bundle and avoids the eager load-order collision
const ProjectFinder = lazy(() => import("../../../../gd-components/finder/project/project-finder"))

// Tether (see gd-popover/popover-v2.tsx) sizes and flips/pins the popover once, the moment it mounts -
// it never re-measures afterwards (only on window scroll/resize). The lazy chunk resolving and
// swapping a small loading placeholder for the ~400px project tree happens after that, with nothing
// to tell Tether the popover grew - so on the first open in a session (before the chunk is cached)
// it stays positioned for the placeholder's tiny size and the real content overflows the window.
// Giving the fallback the same footprint as the loaded content (same wrapper SelectParent_Popover
// itself uses for its own loading state, in ../../../select-parent-new/components/popover/popover.tsx)
// means Tether's one-time measurement is already correct, whether the chunk is cached or not.
function FinderLoading() {
    return (
        <div className="flex-row-center-center h-400" style={{width: 575}}>
            <UILoading/>
        </div>
    )
}

type Props = {
    hidePopover : func
    menuParams  : {
        project     : ProjectSession
        hidePopover : func   // closes the whole "More" menu
    }
}

export default function MoreProject_MovePopover(props: Props) {

    const project = props.menuParams.project

    // the project itself and its subtree can't be the target (same filter as the Move modal)
    const availableProjects = gd.session.projects.filterAvailableProjects([project.id, ...project.childrenProjectsIds])

    // null = the "Projects" space root item (canBeNull), a workspace root comes as its project id
    const onClickProject = (projectId: ID | null) => {
        const targetProject = projectId ? gd.session.projects.get(projectId) : undefined

        props.menuParams.hidePopover()

        // dynamic import -- keeps the "Move to project" model chain (Document/Task/fields3)
        // out of the always-loaded sidebar "More" menu bundle, avoiding an eager load-order
        // collision with the fields3 <-> resources.ts circular dependency (fields3/fields-factory
        // transitively imports resources.ts, which imports model2/doc/document.ts -> fields.ts,
        // which needs FieldsCollection to already be resolved)
        import("../../../../gd-modals/move-to/move").then(({moveProjectToProjectViaPopover}) => {
            moveProjectToProjectViaPopover(project, targetProject)
        })

        return
    }

    return (
        <Suspense fallback={<FinderLoading/>}>
            <ProjectFinder
                noHeader                = {true}
                parentsFor              = {EntityType.PROJECT}
                availableProjects       = {availableProjects}
                displayWorkspaceRoot    = {true}
                canBeNull               = {true}
                parentProjectId         = {project.parentId || undefined}
                currentProjectId        = {project.id}
                onClickProject          = {onClickProject}
                onClose                 = {props.hidePopover}
            />
        </Suspense>
    )
}
